import { NgRedux } from '@ptl/angular-redux-store';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENTITLEMENTS, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, USER_ROLE } from '@ptl/account-management';
import { Brand, BrandingConfigurationService } from '@ptl/brand';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, NormalizedResponse, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ProducerStateStructure } from '../../producer/configs/interfaces/producerStateStructure.interface';
import { Agent, AppDataPayload, AppDataPayloadParsed, OnExchangeApplicantSummary } from '../interfaces/on-exchange-applicant-summary.interface';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME, resetStateOnExchangeEligibilityReducerName, setAppStatus, setData, setFFMData, setIndividualResponsibilityAmount, setSelectedMonthlyAptcForProspect } from '../reducers/eligibility.reducer';
//import * as moment from 'moment';
import moment from 'moment';
import { ONXEligibilityResponseApplicant, EligibilityResponsePayload, EligibilityRenderable } from '../interfaces/eligibility.interface';
import { EnrollManagerService } from './enroll-manager.service';
import { Subject } from 'rxjs';
import { OnExchangeApplicationStatus, OnExchangeEnrollResponse } from '../interfaces/on-exchange-enrollment.response-payload.interface';
import { setEffectiveDate } from '../../shared/components/configs/reducers/effective-date.reducer';

@Injectable({
  providedIn: 'root'
})
export class EligibilityManagerService {

  selectedAPTCChangedSubject: Subject<string> = new Subject();

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService,
    private commonUtilityService: CommonUtilityService,
    private entitlementService: EntitlementsHelperService,
    private ngRedux: NgRedux<StateStructure>,
    private enrollManagerService: EnrollManagerService
  ) { }

  get onExchange(): AppDataPayloadParsed {
    const eligibilityStateStructure: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const currentProspectId = eligibilityStateStructure.currentProspectId;
    const matchedProspects = eligibilityStateStructure.prospects.filter(p => p.prospectId === currentProspectId);
    if (matchedProspects.length > 0) {
      return matchedProspects[0].data as AppDataPayloadParsed;
    } else {
      return null;
    }
  }
  /**
   * EligibilityManager is a model object which saves the
   * eligibility data into a closured space. When the data is needed
   * it will either reach into that variable or it will go to the proper service
   *
   * Usage:
   *    EligibilityManager.get().then(function() { });
   *    EligibilityManager.set( dataFromFFM );
   */
  async getOnExchangeApplicantSummary(
    prospectId: string,
    statusVal?: string,
    selectedMarket?: LINE_OF_BUSINESS_ENUM
  ): Promise<NormalizedResponse<OnExchangeApplicantSummary>> {

    const loginInfo: UserInfoStateStructure
      = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    const postPayload = await (async () => {

      const payload: { prospectId: string; agencyId?: string; npn?: string; status?: string; } = { prospectId };

      const isProducer = this.commonUtilityService.isAgent();
      if (isProducer) {

        if (!selectedMarket) {
          console.warn('Be sure to include the CompactProductAgency on a page where this is used');
          return;
        }

        const producerInfo: ProducerStateStructure = this.reducerUtilityService.getState('Producer');

        const hasGAAEntitlement: boolean
          = await this.entitlementService.entitlementValueForSelectedAgencyByNameAndLob(
            ENTITLEMENTS.GENERAL_AGENCY_ADMIN,
            selectedMarket, loginInfo?.userInfo?.role as USER_ROLE
          );

        const hasTelesalesEntitlement: boolean
          = await this.entitlementService.entitlementValueForSelectedAgencyByNameAndLob(
            ENTITLEMENTS.TELESALES_AGENT_DP,
            selectedMarket, loginInfo?.userInfo?.role as USER_ROLE
          );

        const hasRetailStoreEntitlement: boolean
          = await this.entitlementService.entitlementValueForSelectedAgencyByNameAndLob(
            ENTITLEMENTS.RETAIL_STORE,
            selectedMarket, loginInfo?.userInfo?.role as USER_ROLE
          );

        if (!(hasGAAEntitlement || hasTelesalesEntitlement || hasRetailStoreEntitlement)) {
          payload.npn = producerInfo?.selectedAgency?.npnNo || loginInfo?.userInfo?.npnNo;
        }

        payload.agencyId = producerInfo?.selectedAgency?.agencyId;
        payload.status = statusVal;

      }

      return payload;

    })();
    const requestConfigImplementation = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/cmcrst/x-services/applicant/onexchange/summary',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: postPayload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest<NormalizedResponse<OnExchangeApplicantSummary>>(requestConfigImplementation)
  }

  async get(
    prospectId: string,
    status?: string,
    selectedMarket?: LINE_OF_BUSINESS_ENUM
  ): Promise<EligibilityRenderable> {

    // Determines if onExchange has been retrieve from the service.
    // If it has been resolved, return the data without going to the service
    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];

    const storageDataFiltered = eligibilityState.prospects.filter(p => p.prospectId === prospectId);
    const storageData = storageDataFiltered.length > 0 ? storageDataFiltered[0].data : null;
    const ffmStorageData = storageDataFiltered.length > 0 ? storageDataFiltered[0].ffmData : null;
    const appStatusStorageData = storageDataFiltered.length > 0 ? storageDataFiltered[0].appStatus : null;

    if (!storageData) {

      const onxSummaryResponse
        = await this.getOnExchangeApplicantSummary(
          prospectId, status, selectedMarket
        );

      if (onxSummaryResponse instanceof HttpErrorResponse) {
        this.set(null, prospectId);
        return;
      }

      /** Parse APPDATA */
      const appDataPayloadStringified: string = onxSummaryResponse.payload.appData;
      const appDataPayloadParsed: AppDataPayload = JSON.parse(appDataPayloadStringified);
      let appDataPayloadFinalParsed: AppDataPayloadParsed = null;
      if (appDataPayloadParsed.appData) {
        appDataPayloadFinalParsed = JSON.parse(appDataPayloadParsed.appData);
      } else {
        throw new Error('No app data in the object');
      }

      /** Parse FFMRESPONSE */
      const ffmResponsePayloadStringified: string = onxSummaryResponse.payload.ffmResponse;
      const ffmresponsePayloadFinalParsed: OnExchangeEnrollResponse
        = ffmResponsePayloadStringified ? JSON.parse(ffmResponsePayloadStringified) : null;

      const appStatus: OnExchangeApplicationStatus = onxSummaryResponse.payload.appStatus;

      this.setApplication(appDataPayloadFinalParsed, ffmresponsePayloadFinalParsed, appStatus, prospectId);

      this.setFFMData(ffmresponsePayloadFinalParsed, prospectId);
      this.setAppStatus(appStatus, prospectId);
      this.set(appDataPayloadFinalParsed, prospectId);

      return appDataPayloadFinalParsed;
    } else {

      this.setFFMData(ffmStorageData, prospectId);
      this.setAppStatus(appStatusStorageData, prospectId);
      this.set(storageData, prospectId);

      this.setApplication(storageData as AppDataPayloadParsed, ffmStorageData, appStatusStorageData, prospectId);
      return storageData;

    }
  }
  setFFMData(data: OnExchangeEnrollResponse, prospectId: string) {
    this.ngRedux.dispatch(setFFMData(prospectId, data));
  }
  setAppStatus(data: OnExchangeApplicationStatus, prospectId: string) {
    this.ngRedux.dispatch(setAppStatus(prospectId, data));
  }
  set(data: EligibilityResponsePayload, prospectId: string) {

    // Update final effective date in the state for confirmation page use
    this.ngRedux.dispatch(setEffectiveDate(data.applicants[0].effectiveDate));
    console.log('VALIDATE SBCS ARE GETTING CORRECT EFFECTIVE DATE, NOW');

    this.ngRedux.dispatch(setData(prospectId, data));
    // this.onExchange = data as AppDataPayloadParsed;
  }
  setApplication(
    appData: AppDataPayloadParsed,
    ffmData: OnExchangeEnrollResponse,
    appStatus: OnExchangeApplicationStatus,
    prospectId: string
  ) {
    if (!this.enrollManagerService.hasApplication() && appData.selectedPlan) {
      this.enrollManagerService.setOnExchangeApplication(appData, prospectId);

      if (ffmData) {
        this.enrollManagerService.setPaymentTransactionId(ffmData);
        this.enrollManagerService.setFFMPolicyNumber(ffmData);
        this.enrollManagerService.setAppStatus(appStatus);
        this.enrollManagerService.setSubmissionDate(appData.submissionDate);
      }
    }
  }
  getAgentInformation(): Agent {
    if (this.onExchange.agent && this.onExchange.agent.fullName) {
      return {
        fullName: {
          firstName: this.onExchange.agent.fullName.firstName,
          lastName: this.onExchange.agent.fullName.lastName
        },
        npn: this.onExchange.agent.npn
      };
    } else {
      return {
        fullName: {
          firstName: null,
          lastName: null
        },
        npn: null
      };
    }
  }
  setAgentInformation(prospectId: string, agentInformation: { firstName: string, lastName: string, npn: string }) {
    this.onExchange.agent = {
      fullName: {
        firstName: agentInformation.firstName,
        lastName: agentInformation.lastName
      },
      npn: agentInformation.npn
    };

    this.set(this.onExchange, prospectId);
  }
  setPartnerWebsiteUserCode(prospectId: string, partnerWebsiteUserCode: string) {
    this.onExchange.partnerWebsiteUserCode = partnerWebsiteUserCode;
    this.set(this.onExchange, prospectId);
  }
  flush(prospectId: string) {
    this.ngRedux.dispatch(resetStateOnExchangeEligibilityReducerName(prospectId));
    // this.onExchange = null;
    console.log('eligibility manager flushed for ' + prospectId);
  }
  getPrimaryApplicant(): ONXEligibilityResponseApplicant {
    let primaryApplicant: ONXEligibilityResponseApplicant;

    this.onExchange.applicants.some(person => {
      if (person.isPrimary) {
        primaryApplicant = person;
      }
    });

    return primaryApplicant;
  }
  getPrimaryApplicantZipcodeAndCounty() {
    let index = 0;

    for (let i = 0; i < this.onExchange.applicants.length; i++) {
      if (this.onExchange.applicants[i].isPrimary) {
        index = i;
        break;
      }
    }

    return {
      zip: this.onExchange.applicants[index].homeAddress.zipCode,
      county: this.onExchange.applicants[index].homeAddress.county
    };

  }
  getRelationshipCodeBasedOnPrimary(applicant: ONXEligibilityResponseApplicant): string {
    const primaryApplicant = this.getPrimaryApplicant();
    let returnedCode: string;

    primaryApplicant.familyCodes.forEach(codeObject => {
      if (codeObject.person === applicant.personID) {
        returnedCode = codeObject.codeValue;
      }
    });

    return returnedCode;
  }
  getEnrollmentPeriodForApplicant(applicant: ONXEligibilityResponseApplicant) {
    const enrollmentPeriodCodeMap = {
      AEP: 'Annual',
      IEP: 'Initial',
      SEP: 'Special'
    };

    if (applicant.AEP) {
      return enrollmentPeriodCodeMap.AEP;
    } else if (applicant.IEP) {
      return enrollmentPeriodCodeMap.IEP;
    } else if (applicant.SEP) {
      return enrollmentPeriodCodeMap.SEP;
    }
  }
  getNumberOfAdultsAndChildren() {
    const retVal = {
      adults: 0,
      children: 0
    };

    const currentBrand: Brand = this.brandService.getCurrentBrand();

    for (let i = 0; i < this.onExchange.applicants.length; i++) {

      const effectiveDateAsMoment = moment(this.onExchange.effectiveDate);
      const applicantDOBAsMoment = moment(this.onExchange.applicants[i].dateOfBirth);
      const differenceInYears = effectiveDateAsMoment.diff(applicantDOBAsMoment, 'year');
      const isAdult = currentBrand.individualMarketConstants.ageOfAdulthood < differenceInYears;

      if (isAdult) {
        retVal.adults += 1;
      } else {
        retVal.children += 1;
      }
    }

    return retVal;
  }
  getNumberOfEligibleAdultsAndChildren() {
    const retVal = {
      adults: 0,
      children: 0
    };

    const currentBrand: Brand = this.brandService.getCurrentBrand();

    for (let i = 0; i < this.onExchange.applicants.length; i++) {
      if (this.onExchange.applicants[i].isEligible) {

        const effectiveDateAsMoment = moment(this.onExchange.effectiveDate);
        const applicantDOBAsMoment = moment(this.onExchange.applicants[i].dateOfBirth);
        const differenceInYears = effectiveDateAsMoment.diff(applicantDOBAsMoment, 'year');
        const isAdult = currentBrand.individualMarketConstants.ageOfAdulthood < differenceInYears;

        if (isAdult) {
          retVal.adults += 1;
        } else {
          retVal.children += 1;
        }
      }
    }

    return retVal;
  }
  setSelectedMonthlyAptcForProspect(prospectId: string, selectedMonthlyAptc: number) {
    this.ngRedux.dispatch(setSelectedMonthlyAptcForProspect(prospectId, selectedMonthlyAptc));
    this.selectedAPTCChangedSubject.next(prospectId);
  }
  getSelectedMonthlyAptcForProspect(prospectId: string) {
    const onExchangeEligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = onExchangeEligibilityState.prospects.filter(p => p.prospectId === prospectId);
    if (matchedProspects.length > 0) {
      return matchedProspects[0].selectedMonthlyAptcForProspect;
    }
  }
  /**
   * When you choose a plan to enroll with, for ONX, store
   * data related to the premium/aptc savings as appropriate
   */
  updateIndividualResponsibilityAmount(monthlyPremium: string, ehb: string = '1') {
    const onExchangeEligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const currentProspectId = onExchangeEligibilityState.currentProspectId;
    const matchedProspects
      = onExchangeEligibilityState.prospects
        .filter(p => p.prospectId === currentProspectId);


    const premium = Number(monthlyPremium);
    let selectedAPTC = 0.00;
    let appliedAPTC = selectedAPTC;
    let individualResponsibilityAmount: string = null;

    if (matchedProspects[0].selectedMonthlyAptcForProspect) {
      selectedAPTC = Number(Number(matchedProspects[0].selectedMonthlyAptcForProspect).toFixed(2));
    }

    const ehbAmount = premium * Number(ehb);
    if (selectedAPTC < ehbAmount) {
      appliedAPTC = selectedAPTC;
      individualResponsibilityAmount = (premium - selectedAPTC).toFixed(2);
    } else {
      /** If you CAN save more, only save the maximum amount - only employers are giving money AND insurance */
      appliedAPTC = ehbAmount;
      individualResponsibilityAmount = (premium - ehbAmount).toFixed(2);
    }

    this.ngRedux.dispatch(setIndividualResponsibilityAmount(currentProspectId, individualResponsibilityAmount, appliedAPTC));
  }

}
